import type {
  ServiceComponent,
  ConnectionComponent,
} from "@/types/component-types";

// Built-in workflow primitives that aren't owned by any backend product — they're always
// available to every org. Kept as static constants so the components library renders them
// even with zero subscriptions.

const EPOCH = "1970-01-01T00:00:00.000Z";

export const SYSTEM_SERVICES: ServiceComponent[] = [
  {
    id: "email-service",
    name: "Send Email",
    description: "Send emails via SMTP, Mailchimp, SendGrid",
    type: "service",
    source: "system",
    category: "Communication",
    icon: "bi-envelope",
    version: "1.0.0",
    isGeneric: true,
    tags: ["email", "smtp", "communication"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    serviceType: "email",
  },
  {
    id: "sms-service",
    name: "Send SMS",
    description: "Send text messages via Twilio, SMS gateways",
    type: "service",
    source: "system",
    category: "Communication",
    icon: "bi-chat-text",
    version: "1.0.0",
    isGeneric: true,
    tags: ["sms", "text", "mobile", "communication"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    serviceType: "sms",
  },
  {
    id: "webhook-service",
    name: "Webhook",
    description: "Send an HTTP request; the vendor posts the answer back",
    type: "service",
    source: "system",
    category: "Integrations",
    icon: "bi-link-45deg",
    version: "1.0.0",
    isGeneric: true,
    tags: ["webhook", "http", "api"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    serviceType: "webhook",
  },
  {
    id: "condition-service",
    name: "Condition",
    description: "Conditional branching logic",
    type: "service",
    source: "system",
    category: "Logic & Flow",
    icon: "bi-question-diamond",
    version: "1.0.0",
    isGeneric: true,
    tags: ["condition", "logic", "branch"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    serviceType: "condition",
  },
  {
    id: "delay-service",
    name: "Delay",
    description: "Wait for a specified time",
    type: "service",
    source: "system",
    category: "Logic & Flow",
    icon: "bi-clock",
    version: "1.0.0",
    isGeneric: true,
    tags: ["delay", "wait", "timer"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    serviceType: "delay",
  },
];

export const SYSTEM_CONNECTIONS: ConnectionComponent[] = [
  {
    id: "http-connection",
    name: "HTTP Connection",
    description: "Generic HTTP/REST API connection",
    type: "connection",
    source: "system",
    category: "Integrations",
    icon: "bi-link-45deg",
    version: "1.0.0",
    isGeneric: true,
    tags: ["http", "rest", "api"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    provider: "http",
    connectionType: "api",
    authType: "api-key",
    configSchema: {
      type: "object",
      properties: {
        url: { type: "string", description: "Base URL", required: true },
        apiKey: { type: "string", description: "API Key", secret: true },
      },
    },
  },
  {
    id: "database-connection",
    name: "Database Connection",
    description: "Generic database connection (MySQL, PostgreSQL)",
    type: "connection",
    source: "system",
    category: "Data & Storage",
    icon: "bi-database",
    version: "1.0.0",
    isGeneric: true,
    tags: ["database", "sql", "mysql", "postgresql"],
    createdAt: EPOCH,
    updatedAt: EPOCH,
    productId: "system",
    productName: "System",
    productVersion: "1.0.0",
    requiresSubscription: false,
    provider: "database",
    connectionType: "database",
    authType: "username-password",
    configSchema: {
      type: "object",
      properties: {
        host: { type: "string", description: "Database host", required: true },
        port: { type: "number", description: "Database port", required: true },
        database: { type: "string", description: "Database name", required: true },
        username: { type: "string", description: "Username", required: true },
        password: { type: "string", description: "Password", secret: true, required: true },
      },
    },
  },
];
